import { createDispatchFunction, dispatchPromise } from 'shared/helpers';
import { DATE_FORMATS } from 'shared/constants';
import { partial, isEmpty, isUndefined, isNull } from 'lodash';
import moment from 'moment';
import TaskManagementService from './TaskManagementService';

// Actions
const GET_TASKS = 'taskManagement/GET_TASKS';
const GET_ROOM_COMPLETED_TASKS = 'taskManagement/GET_ROOM_TASKS';
const GET_ROOM_STATUS = 'taskManagement/GET_ROOM_STATUS';
const SET_ROOM_STATUS = 'taskManagement/SET_ROOM_STATUS';
const SET_COMPLETE_TASK = 'taskManagement/SET_COMPLETE_TASK';
const SET_EXPIRED_TASK_WARNING = 'taskManagement/SET_EXPIRED_TASK_WARNING';
const TIMER_WARNING = 'taskManagement/TIMER_WARNING';
const PAUSE_TIMER = 'taskManagement/PAUSE_TIMER';
const RESUME_TIMER = 'taskManagement/RESUME_TIMER';
const GET_ROOM_TIMERS = 'taskManagement/GET_ROOM_TIMERS';
const SET_ROOM_TIMER = 'taskManagement/SET_ROOM_TIMER';
const COMPLETE_WARNING_TASK = 'taskManagement/COMPLETE_WARNING_TASK';
const RESET_HIDE_WARNING = 'taskManagement/RESET_HIDE_WARNING';
const GET_REGS_ASSESSMENTS = 'taskManagement/GET_REGS_ASSESSMENTS';
const UPDATE_REG_ASSESSMENT = 'taskManagement/UPDATE_REG_ASSESSMENT';
const GET_ROOM_SUMMARY = 'taskManagement/GET_ROOM_SUMMARY';
const CLEAR_ROOM_STATUS = 'taskManagement/CLEAR_ROOM_STATUS';

const initialState = {
  roomCompletedTasks: [],
  tasks: [],
  roomStatus: {},
  lastCompletedTask: {},
  errorSetTimer: {},
  errorCompleteWarning: {},
  roomTimers: [],
  roomTimersLoading: false,
  hideWarning: {},
  roomSummary: {},
  loadingTasks: false,
  regsAssessments: {},
  loadingRegsAssessments: false,
  errorUpdateAssessment: false,
  errorRegsAssessments: false,
  loadingCompleteTask: false,
};

const timerActions = {
  start: task => ({
    activeRoomTimerStartDateTime: task.completeDateTime,
    activeRoomTimerStartTaskId: task.taskId,
  }),
  stop: () => ({
    activeRoomTimerStartDateTime: null,
    activeRoomTimerStartTaskId: null,
    activeRoomTimerTotalPauseSeconds: null,
    currentPauseId: null,
    currentPauseStartDateTime: null,
  }),
};

const setCompletedToStatus = (data, error, payload, errorStatus) => {
  if (!data || (error && errorStatus !== 400)) {
    return {};
  }
  let newStatus = {
    lastCompletedTaskId: payload.id,
  };
  if (payload.timerAction && payload.timerAction !== 'warning') {
    const taskData = data || {
      taskId: payload.id,
      completeDateTime: moment().format(DATE_FORMATS.dateTimeZone),
    };
    newStatus = {
      ...newStatus,
      ...timerActions[payload.timerAction](taskData),
    };
  }
  return newStatus;
};

const setExpiredTaskOnHideWarning = (data) => {
  if (!data || !data.currentTask) {
    return {};
  }
  
  return {
    lastCompletedTaskId: data.currentTask.id,
  };
};

const setPauseData = (data) => {
  if (!data) {
    return {};
  }

  const pause = data.roomTimerPause.find(p => !p.unpauseDateTime);

  return {
    currentPauseId: pause.id,
    currentPauseStartDateTime: pause.pauseDateTime,
    currentPausePersonnelId: pause.pausePersonnelId,
    currentUnpauseStartDateTime: undefined,
  };
};

const setResumeData = (data, roomStatus) => {
  if (!data) {
    return {};
  }

  const pause = data.roomTimerPause.find(p => p.id === roomStatus.currentPauseId);
  const pauseTime = moment
    .duration(moment(pause.unpauseDateTime).diff(moment(pause.pauseDateTime))).asSeconds();

  return {
    currentUnpauseStartDateTime: pause.unpauseDateTime,
    activeRoomTimerTotalPauseSeconds: roomStatus.activeRoomTimerTotalPauseSeconds + pauseTime,
  };
};

const updateTaskOnHideWarning = (newData, state) => {
  let lastCompletedTask = {};
  const { hideWarning, roomStatus, tasks } = state;

  if (!isEmpty(hideWarning)) {
    if (newData && newData.activeRoomTimerStartTaskId === roomStatus.activeRoomTimerStartTaskId &&
      newData.roomName === roomStatus.roomName) {
      const lastTaskFromStatus = tasks.find(task => task.id === newData.lastCompletedTaskId);
      if (lastTaskFromStatus.displayOrder >= hideWarning.displayOrder) {
        lastCompletedTask = {
          lastCompletedTaskId: newData.lastCompletedTaskId,
          hideWarning: {}
        };
      } else {
        lastCompletedTask = {
          lastCompletedTaskId: roomStatus.lastCompletedTaskId,
        }
      }
    }
  }

  return lastCompletedTask;
};

// Reducer
export default function taskManagementReducer(state = initialState, action = {}) {
  switch (action.type) {
    case GET_ROOM_COMPLETED_TASKS:
      return {
        ...state,
        roomCompletedTasks: action.result.data || [],
      };
    case GET_TASKS:
      return {
        ...state,
        tasks: action.result.data || [],
        loadingTasks: action.result.loading,
      };
    case GET_ROOM_STATUS:
      return {
        ...state,
        roomStatus: {
          loading: action.result.loading,
          ...action.result.data || {...state.roomStatus},
          ...updateTaskOnHideWarning(action.result.data, state)
        }
      };
    case SET_ROOM_STATUS:
      return {
        ...state,
        roomStatus: {
          ...state.roomStatus,
          ...action.result.data || {},
        },
      };
    case SET_COMPLETE_TASK:
      return {
        ...state,
        timerWarning: false,
        roomStatus: {
          ...state.roomStatus,
          ...setCompletedToStatus(action.result.data, action.result.error, action.result.payload, action.result.errorStatus),
        },
        loadingCompleteTask: action.result.loading,
        hideWarning: {},
      };
    case RESET_HIDE_WARNING:
      return {
        ...state,
        hideWarning: {},
      };
    case SET_EXPIRED_TASK_WARNING:
      return {
        ...state,
        timerWarning: false,
        roomStatus: {
          ...state.roomStatus,
          ...setExpiredTaskOnHideWarning(action.result),
        },
        hideWarning: action.result ? action.result.warning : {},
      };
    case TIMER_WARNING:
      return {
        ...state,
        timerWarning: action.value,
      };
    case PAUSE_TIMER:
      return {
        ...state,
        roomStatus: {
          ...state.roomStatus,
          ...setPauseData(action.result.data),
          // ...updateTaskOnHideWarning(state.roomStatus, state.hideWarning),
        },
      };
    case RESUME_TIMER:
      return {
        ...state,
        roomStatus: {
          ...state.roomStatus,
          ...setResumeData(action.result.data, state.roomStatus),
          // ...updateTaskOnHideWarning(state.roomStatus, state.hideWarning),
        },
      };
    case GET_ROOM_TIMERS:
      return {
        ...state,
        roomTimers: action.result.data || [],
        roomTimersLoading: action.result.loading,
      };
    case SET_ROOM_TIMER:
      return {
        ...state,
        errorSetTimer: action.result.error,
      };
    case COMPLETE_WARNING_TASK:
      return {
        ...state,
        errorCompleteWarning: action.result.error,
      };
    case GET_REGS_ASSESSMENTS:
      return {
        ...state,
        regsAssessments: action.result.data || {},
        loadingRegsAssessments: action.result.loading || false,
        errorRegsAssessments: !!action.result.error || !isEmpty(action.result.error),
      };
    case UPDATE_REG_ASSESSMENT:
      return {
        ...state,
        errorUpdateAssessment: !isUndefined(action.result.error) && !isNull(action.result.error),
      };
    case GET_ROOM_SUMMARY:
      return {
        ...state,
        roomSummary: action.result.data,
      };
    case CLEAR_ROOM_STATUS:
      return {
        ...state,
        roomStatus: {},
      };
    default:
      return state;
  }
}

// Action Creators

export function getTasksAction(result) {
  return { type: GET_TASKS, result };
}

export function getRoomCompletedTasksAction(result) {
  return { type: GET_ROOM_COMPLETED_TASKS, result };
}

export function getRoomStatusAction(result) {
  return { type: GET_ROOM_STATUS, result };
}

export function setRoomStatusAction(result) {
  return { type: SET_ROOM_STATUS, result };
}

export function setCompleteTaskAction(result) {
  return { type: SET_COMPLETE_TASK, result };
}

export function setExpiredTaskWarningAction(result) {
  return { type: SET_EXPIRED_TASK_WARNING, result };
}

export function timerWarningAction(value) {
  return { type: TIMER_WARNING, value };
}

export function resetHideWarningAction(value) {
  return { type: RESET_HIDE_WARNING, value };
}

export function pauseTimerAction(result) {
  return { type: PAUSE_TIMER, result };
}

export function resumeTimerAction(result) {
  return { type: RESUME_TIMER, result };
}

export function getRoomTimersAction(result) {
  return { type: GET_ROOM_TIMERS, result };
}

export function setRoomTimerAction(result) {
  return { type: SET_ROOM_TIMER, result };
}

export function completeWarningTaskAction(result) {
  return { type: COMPLETE_WARNING_TASK, result };
}

export function getRegsAssessmentsAction(result) {
  return { type: GET_REGS_ASSESSMENTS, result }
}

export function updateRegAssessmentAction(result) {
  return { type: UPDATE_REG_ASSESSMENT, result }
}

export function getRoomSummaryAction(result) {
  return { type: GET_ROOM_SUMMARY, result }
}

export function clearRoomStatusAction() {
  return { type: CLEAR_ROOM_STATUS }
}

// Action dispatcher functions

export function getTasks(cycleId) {
  return (dispatch, getState) => {
    const { taskManagement } = getState();
    if (taskManagement.tasks.length && taskManagement.tasks[0].cycleId === cycleId) {
      return dispatch(getTasksAction({ data: taskManagement.tasks }));
    }

    return dispatchPromise(
      dispatch,
      partial(TaskManagementService.getTasks, cycleId),
      getTasksAction
    );
  };
}

export function getRoomCompletedTasks(id) {
  return createDispatchFunction(
    partial(TaskManagementService.getRoomCompletedTasks, id),
    getRoomCompletedTasksAction
  );
}

export function getRoomStatus(roomId) {
  return function (dispatch, getState) {
    const { roomStatus } = getState().taskManagement;
    if (roomStatus.loading) {
      return roomStatus;
    }

    return dispatchPromise(
      dispatch,
      partial(TaskManagementService.getRoomStatus, roomId),
      getRoomStatusAction
    );
  };
}

export function setCompleteTask(roomId, task) {
  return createDispatchFunction(
    partial(TaskManagementService.setCompleteTask, roomId, task.id),
    setCompleteTaskAction,
    task
  );
}

export function setExpiredTaskWarning(task) {
  return function (dispatch) {
    return dispatch(setExpiredTaskWarningAction(task));
  };
}

export function pauseTimer(roomId) {
  return createDispatchFunction(
    partial(TaskManagementService.pauseTimer, roomId),
    pauseTimerAction
  );
}

export function resumeTimer(roomId, irregularity) {
  return createDispatchFunction(
    partial(TaskManagementService.resumeTimer, roomId, irregularity),
    resumeTimerAction
  );
}

export function getRoomTimers(roomId) {
  return createDispatchFunction(
    partial(TaskManagementService.getRoomTimers, roomId),
    getRoomTimersAction
  );
}

export function setRoomTimer(roomId, data) {
  return createDispatchFunction(
    partial(TaskManagementService.setRoomTimer, roomId, data),
    setRoomTimerAction
  );
}


export function completeWarningTask(roomId, data) {
  return createDispatchFunction(
    partial(TaskManagementService.completeWarningTask, roomId, data),
    completeWarningTaskAction
  );
}

export function getRegsAssessments(regId, tcEventId) {
  return createDispatchFunction(
    partial(TaskManagementService.getRegsAssessments, regId, tcEventId),
    getRegsAssessmentsAction
  );
}

export function updateRegAssessment(regId, data) {
  return createDispatchFunction(
    partial(TaskManagementService.updateRegAssessment, regId, data),
    updateRegAssessmentAction
  );
}

export function getRoomSummary(roomId) {
  return createDispatchFunction(
    partial(TaskManagementService.getRoomSummary, roomId),
    getRoomSummaryAction
  );
}